import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DicomMetadataStore, MODULE_TYPES, useSystem } from '@ohif/core';
import Dropzone from 'react-dropzone';
import filesToStudies from './filesToStudies';
import { extensionManager } from '../../App';
import logo from '../../../assets/logo.png';

const P = 'hsl(var(--primary))';
const PL = 'hsl(var(--primary))';

type LocalProps = { modePath: string };

function Local({ modePath }: LocalProps) {
  const { servicesManager } = useSystem();
  const { customizationService } = servicesManager.services;
  const navigate = useNavigate();
  const dropzoneRef = useRef();
  const [dropInitiated, setDropInitiated] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  const LoadingIndicatorProgress = customizationService.getCustomization('ui.loadingIndicatorProgress');
  const dataSourceModules = extensionManager.modules[MODULE_TYPES.DATA_SOURCE];
  const localDataSources = dataSourceModules.reduce((acc, curr) => {
    curr.module.forEach(mod => { if (mod.type === 'localApi') acc.push(mod); });
    return acc;
  }, []);
  const dataSource = localDataSources[0].createDataSource({});
  const microscopyExtensionLoaded = extensionManager.registeredExtensionIds.includes('@ohif/extension-dicom-microscopy');

  const onDrop = async acceptedFiles => {
    const studies = await filesToStudies(acceptedFiles, dataSource);
    const query = new URLSearchParams();
    if (microscopyExtensionLoaded) {
      const smStudies = studies.filter(id => {
        const study = DicomMetadataStore.getStudy(id);
        return study.series.findIndex(s => s.Modality === 'SM' || s.instances[0].Modality === 'SM') >= 0;
      });
      if (smStudies.length > 0) { smStudies.forEach(id => query.append('StudyInstanceUIDs', id)); modePath = 'microscopy'; }
    }
    studies.forEach(id => query.append('StudyInstanceUIDs', id));
    query.append('datasources', 'dicomlocal');
    navigate(`/${modePath}?${decodeURIComponent(query.toString())}`);
  };

  useEffect(() => {
    document.body.style.cssText = 'background:hsl(var(--background));margin:0;overflow:auto';
    return () => { document.body.style.cssText = ''; };
  }, []);

  const ctImgs = [
    { src: '/assets/ct-images/xray-doctor.jpg', label: 'X-Ray Analysis' },
    { src: '/assets/ct-images/mri-scans.jpg', label: 'MRI Scans' },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        .hl-root * { box-sizing: border-box; margin: 0; }
        .hl-root { font-family: 'Inter', system-ui, sans-serif; }

        /* ── Animations ── */
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes gentleFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes scanBeam {
          0% { top: -2px; opacity: 0; }
          5% { opacity: 0.8; }
          35% { opacity: 0.8; }
          40% { top: 100%; opacity: 0; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes orbGlow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.15); }
        }
        @keyframes borderDash {
          0% { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: -20; }
        }
        @keyframes pulseRing {
          0% { transform: scale(1); opacity: 0.5; }
          100% { transform: scale(1.6); opacity: 0; }
        }

        /* ── Floating CT Cards ── */
        .ct-float {
          position: absolute; z-index: 0;
          width: 320px; height: 550px;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.06);
          box-shadow: 0 8px 40px rgba(0,0,0,0.4);
          opacity: 0;
          animation: fadeIn 1.2s ease forwards;
        }
        .ct-float img {
          width: 100%; height: 100%; object-fit: cover; display: block;
          filter: brightness(0.5) saturate(0.7);
          transition: filter 0.6s ease;
        }
        .ct-float:hover img {
          filter: brightness(0.75) saturate(1);
        }
        .ct-float .scan-line {
          position: absolute; left: 0; right: 0; height: 1px;
          background: linear-gradient(90deg, transparent, ${PL}, transparent);
          box-shadow: 0 0 12px ${PL}, 0 0 30px hsl(var(--primary)/0.15);
          pointer-events: none;
          animation: scanBeam 10s ease-in-out infinite;
        }
        .ct-float .cell-label {
          position: absolute; bottom: 0; left: 0; right: 0;
          padding: 20px 12px 8px;
          background: linear-gradient(transparent, rgba(0,0,0,0.6));
          font-size: 10px; font-weight: 600; color: rgba(255,255,255,0.4);
          letter-spacing: 1px; text-transform: uppercase;
        }


        /* ── Content ── */
        .content-layer {
          position: relative; z-index: 1;
          width: 100%; min-height: 100vh;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          padding: 24px 16px;
          gap: 0;
        }

        /* ── Logo area ── */
        .logo-bar {
          flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          opacity: 0;
          animation: fadeUp 0.8s ease 0.2s forwards;
          margin-bottom: 14px;
        }
        .logo-pill {
          display: flex; align-items: center; gap: 12px;
        }
        .logo-pill img {
          height: 80px;
          filter: brightness(0) invert(1);
          opacity: 0.95;
        }

        /* ── Hero text ── */
        .hero-text {
          text-align: center; margin-bottom: 24px;
          opacity: 0;
          animation: fadeUp 0.8s ease 0.4s forwards;
        }
        .hero-text h1 {
          font-size: 36px; font-weight: 900; color: #fff;
          letter-spacing: -1.5px; line-height: 1.1;
          margin-bottom: 8px;
        }
        .hero-text h1 span {
          background: linear-gradient(135deg, ${PL}, hsl(var(--secondary)), ${PL});
          background-size: 200% 100%;
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 4s linear infinite;
        }
        .hero-text p {
          font-size: 15px; color: rgba(255,255,255,0.35);
          font-weight: 400; line-height: 1.6;
        }

        /* ── Upload card ── */
        .upload-card {
          width: 520px; max-width: 92vw;
          background: hsl(var(--card)/0.8);
          backdrop-filter: blur(40px);
          -webkit-backdrop-filter: blur(40px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 20px;
          padding: 32px;
          position: relative;
          opacity: 0;
          animation: fadeUp 0.8s ease 0.6s forwards;
          box-shadow: 0 24px 80px rgba(0,0,0,0.5), 0 0 120px hsl(var(--primary)/0.08);
        }
        .upload-card::before {
          content: '';
          position: absolute; inset: -1px;
          border-radius: 21px;
          padding: 1px;
          background: linear-gradient(160deg, hsl(var(--primary)/0.2), transparent 40%, transparent 60%, hsl(var(--primary)/0.1));
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
        }

        .drop-area {
          border: 1.5px dashed rgba(255,255,255,0.08);
          border-radius: 14px;
          padding: 24px 18px;
          text-align: center;
          transition: all 0.4s ease;
          position: relative;
          background: rgba(255,255,255,0.01);
          margin-bottom: 18px;
        }
        .drop-area.drag-active {
          border-color: ${PL};
          background: hsl(var(--primary)/0.08);
          box-shadow: inset 0 0 40px hsl(var(--primary)/0.05);
        }
        .drop-area:hover {
          border-color: hsl(var(--primary)/0.3);
          background: hsl(var(--primary)/0.04);
        }

        .upload-icon-wrap {
          width: 44px; height: 44px; border-radius: 12px;
          background: linear-gradient(135deg, hsl(var(--primary)/0.15), hsl(var(--primary)/0.08));
          border: 1px solid hsl(var(--primary)/0.15);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 16px;
        }

        .btn-row {
          display: flex; gap: 12px;
        }
        .btn-primary {
          flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px;
          padding: 11px 18px; border-radius: 12px; border: none;
          background: linear-gradient(135deg, ${P}, hsl(var(--secondary)));
          color: #fff; font-size: 14px; font-weight: 700;
          cursor: pointer; transition: all 0.3s ease;
          box-shadow: 0 4px 20px hsl(var(--primary)/0.4);
          font-family: inherit;
        }
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 32px hsl(var(--primary)/0.55);
        }
        .btn-secondary {
          flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px;
          padding: 11px 18px; border-radius: 12px;
          border: 1px solid rgba(255,255,255,0.08);
          background: rgba(255,255,255,0.02);
          color: rgba(255,255,255,0.5); font-size: 14px; font-weight: 700;
          cursor: pointer; transition: all 0.3s ease;
          font-family: inherit;
        }
        .btn-secondary:hover {
          transform: translateY(-2px);
          border-color: rgba(255,255,255,0.15);
          background: rgba(255,255,255,0.04);
          color: rgba(255,255,255,0.7);
        }

        /* ── Feature row ── */
        .features-row {
          display: flex; flex-wrap: wrap; gap: 16px 24px; margin-top: 32px;
          justify-content: center;
          opacity: 0;
          animation: fadeUp 0.8s ease 0.8s forwards;
        }
        .feature-item {
          display: flex; align-items: center; gap: 8px;
        }
        .feature-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: ${PL};
          box-shadow: 0 0 8px hsl(var(--primary)/0.4);
        }
        .feature-label {
          font-size: 12px; font-weight: 500; color: rgba(255,255,255,0.3);
          letter-spacing: 0.3px;
        }

        /* ── Ambient orbs ── */
        .orb {
          position: absolute; border-radius: 50%;
          filter: blur(80px); pointer-events: none;
          animation: orbGlow 8s ease-in-out infinite;
        }


        /* ── Bottom bar ── */
        .bottom-bar {
          display: flex; flex-wrap: wrap; justify-content: center; align-items: center; gap: 8px;
          padding: 0 16px;
          margin-top: 28px;
          flex-shrink: 0;
          opacity: 0;
          animation: fadeIn 1s ease 1.2s forwards;
        }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .logo-pill img { height: 60px; }
          .hero-text { margin-bottom: 18px; }
          .hero-text h1 { font-size: 26px; letter-spacing: -1px; }
          .hero-text p { font-size: 13px; }
          .upload-card { padding: 24px 20px; border-radius: 18px; }
          .upload-card::before { border-radius: 19px; }
          .drop-area { padding: 24px 16px; margin-bottom: 16px; }
          .upload-icon-wrap { width: 44px; height: 44px; border-radius: 12px; margin-bottom: 12px; }
          .btn-row { flex-direction: column; gap: 10px; }
          .btn-primary, .btn-secondary { padding: 12px 16px; font-size: 13px; border-radius: 12px; }
          .features-row { gap: 12px 16px; margin-top: 24px; }
          .feature-label { font-size: 11px; }
          .bottom-bar { margin-top: 20px; gap: 6px; }
          .ct-float { display: none; }
        }

        @media (max-height: 700px) {
          .logo-pill img { height: 50px; }
          .logo-bar { margin-bottom: 0; }
          .hero-text { margin-bottom: 14px; }
          .hero-text h1 { font-size: 24px; }
          .upload-card { padding: 18px 16px; }
          .drop-area { padding: 18px 12px; margin-bottom: 12px; }
          .upload-icon-wrap { width: 40px; height: 40px; margin-bottom: 8px; }
          .btn-primary, .btn-secondary { padding: 10px 14px; }
          .features-row { margin-top: 14px; }
          .bottom-bar { margin-top: 12px; }
        }

        @media (max-width: 480px) {
          .logo-pill img { height: 44px; }
          .hero-text h1 { font-size: 22px; }
          .hero-text p { font-size: 12px; }
          .upload-card { padding: 16px 12px; border-radius: 14px; max-width: 96vw; }
          .upload-card::before { border-radius: 15px; }
          .drop-area { padding: 16px 10px; border-radius: 12px; }
          .features-row { gap: 8px 12px; }
        }

      `}</style>

      <Dropzone
        ref={dropzoneRef}
        onDrop={acceptedFiles => { setDropInitiated(true); setIsDragOver(false); onDrop(acceptedFiles); }}
        onDragEnter={() => setIsDragOver(true)}
        onDragLeave={() => setIsDragOver(false)}
        noClick
      >
        {({ getRootProps }) => (
          <div {...getRootProps()} className="hl-root" style={{
            width: '100vw', minHeight: '100vh', overflow: 'auto',
            background: 'hsl(var(--background))', position: 'relative',
          }}>

            {/* ══ Background image ══ */}
            <div style={{
              position: 'fixed', inset: 0, zIndex: 0,
              backgroundImage: 'url(/assets/ct-images/bg-spinal-mri.jpg)',
              backgroundSize: 'cover', backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              opacity: 0.15,
              pointerEvents: 'none',
            }} />

            {/* ══ Ambient orbs ══ */}
            <div className="orb" style={{
              width: 400, height: 400, top: '10%', left: '15%',
              background: `radial-gradient(circle, hsl(var(--primary)/0.12), transparent 70%)`,
            }} />
            <div className="orb" style={{
              width: 350, height: 350, bottom: '10%', right: '20%',
              background: `radial-gradient(circle, hsl(var(--primary)/0.08), transparent 70%)`,
              animationDelay: '4s',
            }} />

            {/* ══ Floating CT cards (currently disabled) ══ */}

            {/* ══ Content ══ */}
            <div className="content-layer">

              {/* ── Logo ── */}
              <div className="logo-bar">
                <div className="logo-pill">
                  <img src={logo} alt="Helical Analytics" />
                </div>
              </div>

              {/* ── Hero text ── */}
              <div className="hero-text">
                <h1>
                  Medical Imaging<br />
                  <span>Reimagined</span>
                </h1>
                <p>Load DICOM studies locally. Private, instant, powerful.</p>
              </div>

              {/* ── Upload card ── */}
              <div className="upload-card">
                {dropInitiated ? (
                  <div style={{ textAlign: 'center' }}>
                    <LoadingIndicatorProgress className="h-full w-full bg-transparent" />
                    <p style={{ color: 'rgba(255,255,255,0.4)', marginTop: '16px', fontSize: '14px' }}>
                      Processing your files...
                    </p>
                  </div>
                ) : (
                  <>
                    <div className={`drop-area ${isDragOver ? 'drag-active' : ''}`}>
                      <div className="upload-icon-wrap">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: 'hsl(var(--primary))' }} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                      </div>
                      <p style={{ fontSize: '15px', fontWeight: 600, color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>
                        Drag & drop your DICOM files
                      </p>
                      <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.2)' }}>
                        or use the buttons below to browse
                      </p>
                    </div>

                    <div className="btn-row">
                      <Dropzone onDrop={onDrop} noDrag>
                        {({ getRootProps: fp, getInputProps: fip }) => (
                          <div {...fp()} style={{ flex: 1, display: 'flex' }}>
                            <button className="btn-primary" style={{ width: '100%' }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                                <polyline points="13 2 13 9 20 9" />
                              </svg>
                              Select Files
                            </button>
                            <input {...fip()} style={{ display: 'none' }} />
                          </div>
                        )}
                      </Dropzone>
                      <Dropzone onDrop={onDrop} noDrag>
                        {({ getRootProps: dp, getInputProps: dip }) => (
                          <div {...dp()} style={{ flex: 1, display: 'flex' }}>
                            <button className="btn-secondary" style={{ width: '100%' }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                              </svg>
                              Select Folder
                            </button>
                            <input {...dip()} webkitdirectory="true" mozdirectory="true" style={{ display: 'none' }} />
                          </div>
                        )}
                      </Dropzone>
                    </div>

                    <p style={{
                      textAlign: 'center', marginTop: '20px',
                      fontSize: '11px', color: 'rgba(255,255,255,0.15)',
                      letterSpacing: '0.5px',
                    }}>
                      Supports DICOM files and folders
                    </p>
                  </>
                )}
              </div>

              {/* ── Features ── */}
              <div className="features-row">
                {['100% Private', 'Instant Load', '3D Rendering', 'Multi-format'].map((f, i) => (
                  <div key={i} className="feature-item">
                    <div className="feature-dot" />
                    <span className="feature-label">{f}</span>
                  </div>
                ))}
              </div>

              {/* ── Bottom bar (in flow) ── */}
              <div className="bottom-bar">
                <span style={{ fontSize: '11px', fontWeight: 500, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.5px' }}>
                  Supported modalities:
                </span>
                {['CT', 'MRI', 'X-Ray', 'PET', 'US'].map((m, i) => (
                  <span key={i} style={{
                    fontSize: '11px', fontWeight: 700, color: 'hsl(var(--primary))',
                    padding: '4px 12px', borderRadius: '20px',
                    background: 'hsl(var(--primary)/0.1)',
                    border: '1px solid hsl(var(--primary)/0.12)',
                  }}>{m}</span>
                ))}
              </div>
            </div>

          </div>
        )}
      </Dropzone>
    </>
  );
}

export default Local;
